import { useMemo } from 'react';
import { useNotify as useDSNotify } from '@hotjar/hotsauce';
import { useTranslation } from 'react-i18next';

type Notify = () => void;

type CustomizedNotify = (message: string) => void;

export interface NotifyFunctions {
  info: CustomizedNotify;
  success: CustomizedNotify;
  error: CustomizedNotify;
  unhandledError: Notify;
}

/**
 * Provides a set of functions that display Notify messages of different types.
 *
 * Usage:
 * const { info, success, error, unhandledError } = useNotify();
 *
 * info('This is the message);
 * success('This is the message);
 * error('This is the message');
 * unhandledError();
 */

export function useNotify(): NotifyFunctions {
  const { showToast } = useDSNotify();
  const { t } = useTranslation();

  return useMemo(
    () => ({
      info(text: string) {
        showToast({
          kind: 'info',
          text,
        });
      },
      success(text: string) {
        showToast({
          kind: 'success',
          text,
        });
      },
      error(text: string) {
        showToast({
          kind: 'danger',
          text,
        });
      },
      unhandledError() {
        showToast({
          kind: 'danger',
          text: t('common:global-notifications.unhandled-error-message'),
        });
      },
    }),
    [showToast, t],
  );
}
